/**
 * Utility functions for extracting candidate names from screening sessions
 * Prioritizes AI-extracted names over transcript parsing
 */

interface ScreeningSession {
  ai_summary?: any;
  transcript?: string;
  candidate_lookup_query?: string;
  candidates?: {
    first_name: string;
    last_name: string;
  } | null;
}

/**
 * Extracts candidate name from AI summary with fallback to transcript parsing
 */
export function extractCandidateName(session: ScreeningSession): string {
  // 1. If linked to candidate, use that name
  if (session.candidates) {
    return `${session.candidates.first_name} ${session.candidates.last_name}`;
  }

  // 2. Try AI summary first (highest priority)
  if (session.ai_summary?.candidate_summary?.name) {
    const aiName = session.ai_summary.candidate_summary.name.trim();
    if (aiName && aiName !== '') {
      return aiName;
    }
  }

  // 3. Fallback to transcript parsing
  const transcriptName = extractNameFromTranscript(session.transcript);
  if (transcriptName) {
    return transcriptName;
  }

  // 4. Use candidate_lookup_query if available
  if (session.candidate_lookup_query) {
    return session.candidate_lookup_query;
  }

  // 5. Final fallback
  return 'Unknown Caller';
}

/**
 * Extracts candidate name from transcript using pattern matching
 * Updated to handle 3+ word names (first, middle, last)
 */
export function extractNameFromTranscript(transcript?: string): string | null {
  if (!transcript) return null;
  
  const namePatterns = [
    // Match 3+ words (first middle last)
    /(?:my name is|i'm|i am|this is)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+[A-Z][a-z]+)/i,
    /(?:i'm|i am)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+[A-Z][a-z]+)/i,
    /hello,?\s+(?:this is|i'm)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+[A-Z][a-z]+)/i,
    // Fallback to 2 words
    /(?:my name is|i'm|i am|this is)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i,
    /(?:i'm|i am)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i,
    /hello,?\s+(?:this is|i'm)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i
  ];
  
  for (const pattern of namePatterns) {
    const match = transcript.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return null;
}

/**
 * Extracts email from transcript with support for "at" format
 */
export function extractEmailFromTranscript(transcript?: string): string | null {
  if (!transcript) return null;
  
  const emailPatterns = [
    // Standard email format
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/,
    // Handle "at" format (e.g., "john.doe at gmail.com")
    /([a-zA-Z0-9._%+-]+)\s+at\s+([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i
  ];
  
  for (const pattern of emailPatterns) {
    const match = transcript.match(pattern);
    if (match) {
      if (match[0].includes(' at ')) {
        // Convert "at" format to standard email
        return `${match[1]}@${match[2]}`;
      }
      return match[1];
    }
  }
  return null;
}

/**
 * Comprehensive candidate info extraction from screening session
 * Prioritizes AI summary over transcript parsing
 */
export function extractCandidateInfoFromSession(session: ScreeningSession): {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  source: 'ai_summary' | 'transcript' | 'none';
} {
  let firstName = '';
  let lastName = '';
  let fullName = '';
  let email = '';
  let source: 'ai_summary' | 'transcript' | 'none' = 'none';

  // 1. Try AI summary first (highest priority)
  if (session.ai_summary?.candidate_summary) {
    const aiSummary = session.ai_summary.candidate_summary;
    
    if (aiSummary.name) {
      fullName = aiSummary.name.trim();
      const nameParts = fullName.split(' ');
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' '); // Handle middle names
        source = 'ai_summary';
      }
    }
    
    if (aiSummary.email && !email) {
      email = aiSummary.email;
    }
  }

  // 2. Fallback to transcript parsing
  if (!fullName && session.transcript) {
    const transcriptName = extractNameFromTranscript(session.transcript);
    if (transcriptName) {
      fullName = transcriptName;
      const nameParts = transcriptName.split(' ');
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' '); // Handle middle names
        source = 'transcript';
      }
    }
  }

  // 3. Extract email if not found in AI summary
  if (!email && session.transcript) {
    const transcriptEmail = extractEmailFromTranscript(session.transcript);
    if (transcriptEmail) {
      email = transcriptEmail;
      if (source === 'none') source = 'transcript';
    }
  }

  return {
    firstName,
    lastName,
    fullName,
    email,
    source
  };
}

/**
 * Gets the display name with confidence indicator
 */
export function getCandidateDisplayName(session: ScreeningSession): {
  name: string;
  source: 'linked' | 'ai_summary' | 'transcript' | 'lookup_query' | 'unknown';
  confidence: 'high' | 'medium' | 'low';
} {
  // Linked candidate (highest confidence)
  if (session.candidates) {
    return {
      name: `${session.candidates.first_name} ${session.candidates.last_name}`,
      source: 'linked',
      confidence: 'high'
    };
  }

  // AI summary (high confidence)
  if (session.ai_summary?.candidate_summary?.name) {
    const aiName = session.ai_summary.candidate_summary.name.trim();
    if (aiName && aiName !== '') {
      return {
        name: aiName,
        source: 'ai_summary',
        confidence: 'high'
      };
    }
  }

  // Transcript parsing (medium confidence)
  const transcriptName = extractNameFromTranscript(session.transcript);
  if (transcriptName) {
    return {
      name: transcriptName,
      source: 'transcript',
      confidence: 'medium'
    };
  }

  // Lookup query (low confidence)
  if (session.candidate_lookup_query) {
    return {
      name: session.candidate_lookup_query,
      source: 'lookup_query',
      confidence: 'low'
    };
  }

  // Unknown (no confidence)
  return {
    name: 'Unknown Caller',
    source: 'unknown',
    confidence: 'low'
  };
}